import { deleteProductsAction, fetchProductsAction } from "./actions";
import { push } from "connected-react-router";
import { FirebaseTimestamp, db } from "../../firebase";
import { v4 as uuidv4 } from "uuid";
import {
  collection,
  doc,
  getDocs,
  deleteDoc,
  orderBy,
  setDoc,
  runTransaction,
  query,
  where,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { getUserByEmail } from "../../functions/users";
import {
  ORDER_NO_COLLECTION,
  ORDER_NO_DOC_ID,
} from "../../functions/orderno";
import { hideLoadingAction, showLoadingAction } from "../loading/actions";
import {
  checkDeductionValidation,
  getFiscalYearStartDate,
  getOrdersFromFiscalYearStart,
  getCategoryMaster,
  getUpdatedDeductionProducts,
} from "../../functions/deducation";
import { setFireStore } from "../../hooks/useCalcDeduction";
import {
  buildOrderData,
  formatOrderNumber,
} from "../../functions/order/buildOrderData";
import {
  getVersionRef,
  isStaleBuildSnap,
  StaleBuildError,
} from "../../functions/appVersion/versionGate";
import { shouldEnforceVersionGate } from "../../functions/appVersion/versionGateCore";
import { BUILD_ID } from "../../generated/buildInfo";
import { isAuthorizedOrderUser } from "../../functions/isAuth";
import { updateUserStateAction } from "../users/actions";

const productsRef = collection(db, "products");

export const deleteProduct = (id) => {
  const ret = window.confirm(
    "商品を削除すると復元はできません よろしいですか？"
  );
  if (!ret) {
    return () => {};
  } else {
    return async (dispatch, getState) => {
      const productDocRef = doc(db, "products", id);
      await deleteDoc(productDocRef).then(() => {
        const prevProducts = getState().products.list;
        const nextProducts = prevProducts.filter(
          (product) => product.id !== id
        );
        dispatch(deleteProductsAction(nextProducts));
      });
    };
  }
};

export const fetchProducts = (gender, category, searchKeywords) => {
  return async (dispatch) => {
    // let q = query(productsRef, orderBy("idin", "desc"));
    let q = query(productsRef, orderBy("orderNo", "asc"));
    q = gender !== "" ? query(q, where("gender", "==", gender)) : q;
    q = category !== "" ? query(q, where("category", "==", category)) : q;
    getDocs(q).then((snapshots) => {
      const productList = [];
      snapshots.forEach((snapshot) => {
        const product = snapshot.data();
        //productList.push(product);
        if (
          product.description.includes(searchKeywords) ||
          product.name.includes(searchKeywords)
        ) {
          productList.push(product);
        }
      });
      // productList.sort((a, b) => Number(a.idin - b.idin));
      dispatch(fetchProductsAction(productList));
    });
  };
};

// 注文トランザクションの業務エラー(理由コード付き)
class OrderTransactionError extends Error {
  constructor(code) {
    super(code);
    this.name = "OrderTransactionError";
    this.code = code;
  }
}

// 同一タブ内での注文確定の多重実行ガード。
// ボタンのdisabledをすり抜けた連打・キー操作によるthunkの二重dispatchを遮断する。
// (別タブ・別端末からの多重実行はトランザクション内のカートdoc存在検証で防ぐ)
let isOrderingInProgress = false;

export const orderProduct = (
  productsInCart,
  amount,
  orderInfo,
  deductionAmount
) => {
  // 戻り値: 注文完了画面へ遷移したときtrue(呼び出し元の画面復帰判定に使う)
  return async (dispatch, getState) => {
    if (isOrderingInProgress) {
      return false;
    }
    isOrderingInProgress = true;
    dispatch(showLoadingAction("注文処理中..."));
    try {
      if (!productsInCart || productsInCart.length === 0) {
        return false;
      }
      if (!(await isOrderInfoValidate(orderInfo, productsInCart))) {
        return false;
      }

      // 確定直前の発注担当者の権限再検証
      // (画面ロード後にXさんが一般化されるなどの競合状態に対応)
      try {
        const orderUserOnUsers = await getUserByEmail(
          orderInfo.order_user_email
        );
        if (!orderUserOnUsers || !isAuthorizedOrderUser(orderUserOnUsers.role)) {
          alert(
            "指定された発注担当者は、現在は発注担当者の権限を保有していません。発注担当者を選び直して、もう一度注文を確定してください。"
          );
          return false;
        }
      } catch (error) {
        console.error("発注担当者の権限確認に失敗しました:", error);
        alert(
          "発注担当者の権限確認に失敗しました。通信環境をご確認のうえ、もう一度お試しください。"
        );
        return false;
      }

      const uid = orderInfo.login_user_uid;

      // 確定直前の控除再判定(購入依頼者有・仕入先無の場合)
      // 画面表示後に同一購入依頼者の別注文が確定した等で控除の前提が変わっていないか、
      // 最新のorders・カテゴリマスタで検算する
      if (
        orderInfo.billing_user_flg === "有" &&
        orderInfo.billing_company_flg !== "有" &&
        orderInfo.billing_user_id
      ) {
        try {
          const { dateObj: firstDate } = getFiscalYearStartDate();
          const freshCategoryCounts = await getOrdersFromFiscalYearStart(
            orderInfo.billing_user_id,
            firstDate
          );
          const categoryIds = [
            ...new Set(productsInCart.map((product) => product.category)),
          ];
          const freshCategoryMaster = await getCategoryMaster(categoryIds);
          const revalidatedProducts = getUpdatedDeductionProducts(
            productsInCart,
            freshCategoryCounts,
            freshCategoryMaster
          );
          const hasDiscrepancy = revalidatedProducts.some(
            (product, index) =>
              product.deduction_id !== productsInCart[index].deduction_id
          );
          if (hasDiscrepancy) {
            // 最新の控除区分をカートdocへ書き戻す(onSnapshot経由で画面表示も最新化される)
            await setFireStore(revalidatedProducts, uid);
            alert(
              "控除区分が更新されました。内容を再確認のうえ、もう一度注文を確定してください。"
            );
            return false;
          }
        } catch (error) {
          console.error("控除の再確認中にエラーが発生しました:", error);
          alert(
            "控除の確認中にエラーが発生しました。通信環境をご確認のうえ、もう一度お試しください。"
          );
          return false;
        }
      }

      const timestamp = Timestamp.now();
      // const testDate = new Date(2024, 4, 31);
      // const timestamp = Timestamp.fromDate(testDate);

      // 注文IDを生成（同じ注文に属する商品で共有する）
      const orderId = uuidv4();

      // 注文作成・カート削除・採番を単一トランザクションで原子的に実行する。
      // カートdocの存在検証により、同じカートを二重に注文化できない(冪等)。
      // 競合時はFirestoreが読み取りからやり直すため、注文番号の重複も起きない。
      await runTransaction(db, async (transaction) => {
        // 読み取り(トランザクションでは全ての読み取りを書き込みより先に行う)
        const orderNoRef = doc(db, ORDER_NO_COLLECTION, ORDER_NO_DOC_ID);
        const enforceVersionGate = shouldEnforceVersionGate(
          window.location.hostname
        );
        const [orderNoSnap, versionSnap] = await Promise.all([
          transaction.get(orderNoRef),
          enforceVersionGate
            ? transaction.get(getVersionRef())
            : Promise.resolve(null),
        ]);
        // このタブのビルドが古い(新ビルド公開済み)なら注文させない
        if (enforceVersionGate && isStaleBuildSnap(versionSnap)) {
          throw new StaleBuildError();
        }
        if (!orderNoSnap.exists()) {
          throw new OrderTransactionError("ORDER_NO_NOT_FOUND");
        }
        const cartRefs = productsInCart.map((product) =>
          doc(db, "users", uid, "cart", product.cartId)
        );
        const cartSnaps = await Promise.all(
          cartRefs.map((cartRef) => transaction.get(cartRef))
        );

        // 検証: カートdocの欠落=別の実行が注文済み(二重確定)、数量不一致=画面が古い
        cartSnaps.forEach((cartSnap, index) => {
          if (!cartSnap.exists()) {
            throw new OrderTransactionError("DUPLICATE_ORDER");
          }
          if (
            Number(cartSnap.data().quantity) !==
            Number(productsInCart[index].quantity)
          ) {
            throw new OrderTransactionError("STALE_CART");
          }
        });

        // 書き込み: 商品ごとに注文docを作成し、カートdocを削除する
        let currentOrderNumber = Number(orderNoSnap.data().order_no);
        productsInCart.forEach((product, index) => {
          const orderRef = doc(collection(db, "orders"));
          // 発注番号を更新
          const orderNumber = formatOrderNumber(++currentOrderNumber);
          transaction.set(
            orderRef,
            buildOrderData({
              orderId,
              orderDocId: orderRef.id,
              orderNumber,
              orderInfo,
              product,
              amount,
              deductionAmount,
              timestamp,
              clientBuildId: BUILD_ID,
            })
          );
          // カートから商品を削除
          transaction.delete(cartRefs[index]);
        });
        transaction.update(orderNoRef, {
          order_no: formatOrderNumber(currentOrderNumber),
        });
      });

      // ログインユーザ(購入依頼者)のusers.order_emailを最新の発注担当者で同期。
      // 失敗しても注文確定自体は成立しているので、ログ出力のみで処理を継続する。
      try {
        const currentOrderEmailOnState = getState().users.order_email;
        const nextOrderEmail = orderInfo.order_user_email;
        if (
          uid &&
          nextOrderEmail &&
          currentOrderEmailOnState !== nextOrderEmail
        ) {
          await updateDoc(doc(db, "users", uid), {
            order_email: nextOrderEmail,
            updated_at: Timestamp.now(),
          });
          dispatch(updateUserStateAction({ order_email: nextOrderEmail }));
        }
      } catch (error) {
        console.error(
          "購入依頼者のorder_email同期に失敗しました(注文自体は成立):",
          error
        );
      }

      dispatch(push("/order/complete"));
      return true;
    } catch (error) {
      if (error instanceof StaleBuildError) {
        // 旧ビルドのタブからの確定操作。再読み込み後の再操作を案内する
        alert(
          "新しいバージョンが公開されています。ページを再読み込みしてから、もう一度注文を確定してください。"
        );
        return false;
      }
      if (
        error instanceof OrderTransactionError &&
        error.code === "DUPLICATE_ORDER"
      ) {
        // 別の実行(連打・別タブ等)がこのカートを注文済み。注文は1件だけ成立している
        alert("この注文はすでに確定されています。注文履歴をご確認ください。");
        dispatch(push("/order/complete"));
        return true;
      }
      if (
        error instanceof OrderTransactionError &&
        error.code === "STALE_CART"
      ) {
        alert(
          "カートの内容が更新されています。内容を再確認のうえ、もう一度注文を確定してください。"
        );
        return false;
      }
      if (error && error.code === "permission-denied") {
        // firestore.rulesのサーバ側ガードで拒否された場合(通常は起きない。
        // 起きるとすればビルドとバージョンdocの不整合)。再読み込みで最新化を促す
        alert(
          "注文を作成できませんでした。ページを再読み込みしてから、もう一度お試しください。"
        );
        return false;
      }
      console.error("注文処理に失敗しました: ", error);
      alert(
        "注文処理に失敗しました。通信環境をご確認のうえ、もう一度お試しください。"
      );
      return false;
    } finally {
      isOrderingInProgress = false;
      dispatch(hideLoadingAction());
    }
  };
};

export const saveProduct = (
  id,
  name,
  description,
  category,
  embroidery,
  price,
  images,
  sizes,
  maker,
  idin,
  idout,
  shop,
  displayProperty,
  orderNo,
  newOrderNo
) => {
  return async (dispatch) => {
    const timestamp = FirebaseTimestamp.now();
    const data = {
      category: category, //カテゴリー
      description: description, //説明
      embroidery: embroidery, //刺繍
      idin: idin, //社内番号
      idout: idout, //社外番号
      images: images, //画像のパス
      maker: maker, //メーカー
      name: name, //名前
      // price: parseInt(price, 10), //10進数
      price: price, //価格
      shop: shop, //販売先
      sizes: sizes, //サイズ
      updated_at: timestamp, //更新時刻
      display: displayProperty,
      //id = newDocRef.id;
      //created_at = timestamp;
      //orderNo = newOrderNo;
    };

    if (id === "") {
      // 新しいドキュメントのリファレンスを生成
      const newDocRef = doc(productsRef);

      // IDと作成日時をデータに追加
      data.id = newDocRef.id;
      data.created_at = timestamp;
      data.orderNo = newOrderNo;

      // データをFirestoreに保存（新規作成）
      return setDoc(newDocRef, data)
        .then(() => {
          dispatch(push("/"));
        })
        .catch((error) => {
          throw new Error(error);
        });
    } else {
      // 既存のドキュメントのリファレンスを生成
      const docRef = doc(productsRef, id);
      data.orderNo = orderNo;
      // データをFirestoreに保存（更新、マージオプションを使用）
      return setDoc(docRef, data, { merge: true })
        .then(() => {
          dispatch(push("/"));
        })
        .catch((error) => {
          throw new Error(error);
        });
    }
  };
};

const isOrderInfoValidate = async (orderInfo, products) => {
  let alertString = "";
  //文字列チェック

  if (
    orderInfo.order_user_id === "" ||
    orderInfo.order_user_name === "" ||
    orderInfo.order_user_email === ""
  ) {
    alertString = "発注担当者";
  }
  if (
    orderInfo.order_base_name === "" ||
    orderInfo.order_department_name === ""
  ) {
    alertString += alertString.length > 0 ? "、発注部署" : "発注部署";
  }
  if (
    orderInfo.billing_user_flg === "有" &&
    (orderInfo.billing_user_id === "" ||
      orderInfo.billing_user_name === "" ||
      !orderInfo.billing_user_name ||
      !orderInfo.billing_user_name)
  ) {
    alertString += alertString.length > 0 ? "、購入依頼者" : "購入依頼者";
  }
  if (
    orderInfo.billing_company_flg === "有" &&
    (orderInfo.billing_company_id === "" ||
      orderInfo.billing_company_name_full === "" ||
      orderInfo.billing_company_name === "" ||
      !orderInfo.billing_company_name ||
      !orderInfo.billing_company_name_full ||
      !orderInfo.billing_company_id)
  ) {
    alertString += alertString.length > 0 ? "、仕入先" : "仕入先";
  }
  console.log({ orderInfo });
  // if (
  //   orderInfo.billing_company_flg === "無" &&
  //   orderInfo.billing_user_flg === "無" &&
  //   orderInfo.remark_on_order === ""
  // ) {
  //   alertString +=
  //     alertString.length > 0 ? "、注文に関する備考" : "注文に関する備考";
  // }

  if (orderInfo.delivery_office_id === "") {
    alertString += alertString.length > 0 ? "、納入先事業所" : "納入先事業所";
  }
  if (
    orderInfo.delivery_department === "" &&
    (orderInfo.delivery_office_id === "50" ||
      orderInfo.delivery_office_id === "51")
  ) {
    alertString += alertString.length > 0 ? "、納入先部署" : "納入先部署";
  }
  //Invalidリターン
  if (alertString.length > 0) {
    alert(alertString + "の入力が不正です");
    return false;
  }
  if (!checkDeductionValidation(products)) {
    alert("控除額の適用が不正です");
    return false;
  }
  return true;
};
